// src/components/categories/AdminCategoryListTab.jsx
// Onglet "Liste" admin : accordéons inline, modifications de masse, filtres avancés
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Paper,
  Grid,
  Divider,
  Avatar,
  Tooltip,
  Alert,
  InputAdornment,
  Collapse,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Category as CategoryIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  HourglassEmpty as PendingIcon,
  DoneAll as BulkApplyIcon,
  RestartAlt as ResetIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseImageIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useCategories } from '../../context/CategoryContext';
import Loader from '../common/Loader';
import ConfirmDialog from '../common/ConfirmDialog';

const API_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:5000/api/v1';

// ─── Constants ────────────────────────────────────────────────────────────────

const OFFER_TYPE_OPTIONS = [
  { value: 'equipment', label: 'Équipement' },
  { value: 'service', label: 'Service' },
  { value: 'ride_share', label: 'Covoiturage' },
  { value: 'property', label: 'Propriété' },
  { value: 'vehicle', label: 'Véhicule' },
  { value: 'product', label: 'Produit' },
];

const CONTEXT_OPTIONS = [
  { value: 'rental', label: 'Location' },
  { value: 'product', label: 'Produit' },
  { value: 'both', label: 'Les deux' },
];

const OFFER_TYPE_LABELS = {
  equipment: 'Équipement',
  service: 'Service',
  ride_share: 'Covoiturage',
  property: 'Propriété',
  vehicle: 'Véhicule',
  product: 'Produit',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getCategoryImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  return `${API_URL}/${imageUrl}`;
};

const getCategoryStatus = (cat) => {
  if (cat.pendingApproval) return 'pending';
  if (!cat.isActive) return 'inactive';
  return 'active';
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'success' },
  inactive: { label: 'Inactive', color: 'default' },
  pending: { label: 'En attente', color: 'warning' },
};

// ─── CategoryEditFields ───────────────────────────────────────────────────────

const CategoryEditFields = ({
  category,
  edits,
  parentOptions,
  onChange,
  onSave,
  onReset,
  saving,
  isDirty,
}) => {
  const [imagePreview, setImagePreview] = useState(null);

  const get = (field) =>
    field in edits ? edits[field] : category[field] ?? '';

  const currentImageUrl = edits.imageFile
    ? imagePreview
    : getCategoryImageUrl(category.imageUrl);

  const handleImageChange = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        onChange('imageFile', file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setImagePreview(null);
    onChange('imageFile', null);
    onChange('imageUrlCleared', true);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Section Image */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              border: '2px dashed',
              borderColor: 'primary.light',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: 'primary.50',
                borderColor: 'primary.main',
              },
            }}
            component="label"
          >
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => handleImageChange(e.target.files?.[0])}
            />
            {currentImageUrl ? (
              <Box>
                <Box
                  component="img"
                  src={currentImageUrl}
                  alt="Aperçu"
                  sx={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mb: 1,
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    component="label"
                  >
                    Changer
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => handleImageChange(e.target.files?.[0])}
                    />
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CloseImageIcon />}
                    onClick={handleClearImage}
                  >
                    Supprimer
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ py: 3 }}>
                <ImageIcon
                  sx={{
                    fontSize: 48,
                    color: 'text.disabled',
                    mb: 1,
                    display: 'block',
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cliquez pour ajouter une image
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Formats acceptés : JPG, PNG, WebP
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Champs texte */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Nom"
            value={get('name')}
            onChange={(e) => onChange('name', e.target.value)}
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Ordre d'affichage"
            type="number"
            value={get('displayOrder')}
            onChange={(e) =>
              onChange('displayOrder', parseInt(e.target.value, 10) || 0)
            }
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Description"
            multiline
            minRows={2}
            maxRows={4}
            value={get('description')}
            onChange={(e) => onChange('description', e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Catégorie parente</InputLabel>
            <Select
              value={get('parentId') || ''}
              label="Catégorie parente"
              onChange={(e) =>
                onChange('parentId', e.target.value || null)
              }
            >
              <MenuItem value="">Aucune (catégorie principale)</MenuItem>
              {parentOptions
                .filter((p) => p.id !== category.id)
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Type d'offre</InputLabel>
            <Select
              value={get('offer_type') || ''}
              label="Type d'offre"
              onChange={(e) => onChange('offer_type', e.target.value)}
            >
              {OFFER_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Contexte</InputLabel>
            <Select
              value={get('context') || 'both'}
              label="Contexte"
              onChange={(e) => onChange('context', e.target.value)}
            >
              {CONTEXT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={!!get('isActive')}
                onChange={(e) => onChange('isActive', e.target.checked)}
                color="success"
              />
            }
            label={get('isActive') ? 'Active' : 'Inactive'}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {isDirty && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ResetIcon />}
            onClick={onReset}
          >
            Annuler
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={onSave}
          disabled={saving || !isDirty}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </Box>
    </Box>
  );
};

// ─── ChildAccordion (sous-catégorie) ─────────────────────────────────────────

const ChildAccordion = ({
  category,
  edits,
  parentOptions,
  isSelected,
  onSelect,
  onChange,
  onSave,
  onReset,
  onDelete,
  isDirty,
  saving,
}) => {
  const [expanded, setExpanded] = useState(false);
  const status = getCategoryStatus(category);
  const statusCfg = STATUS_CONFIG[status];

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((v) => !v)}
      elevation={0}
      sx={{
        border: isDirty ? '2px solid' : '1px solid',
        borderColor: isDirty ? 'warning.main' : 'divider',
        '&:before': { display: 'none' },
        borderRadius: '8px !important',
        mb: 1,
        overflow: 'hidden',
        bgcolor: isDirty ? 'warning.50' : 'background.paper',
        transition: 'border-color 0.2s, background-color 0.2s',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 52,
          pl: 1,
          pr: 2,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1,
            mr: 1,
            overflow: 'hidden',
          },
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(category.id);
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <Avatar
          src={getCategoryImageUrl(category.imageUrl)}
          variant="rounded"
          sx={{ width: 32, height: 32, bgcolor: 'primary.50', flexShrink: 0 }}
        >
          <CategoryIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        </Avatar>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {edits.name ?? category.name}
        </Typography>
        {isDirty && (
          <Tooltip title="Modifications non sauvegardées">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
        <Chip
          size="small"
          label={statusCfg.label}
          color={statusCfg.color}
          sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
        />
        {category.offer_type && (
          <Chip
            size="small"
            label={OFFER_TYPE_LABELS[category.offer_type] ?? category.offer_type}
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Divider sx={{ mb: 2 }} />
        <CategoryEditFields
          category={category}
          edits={edits}
          parentOptions={parentOptions}
          onChange={onChange}
          onSave={onSave}
          onReset={onReset}
          saving={saving}
          isDirty={isDirty}
        />
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => onDelete([category.id])}
          >
            Supprimer
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── ParentAccordion (catégorie principale) ───────────────────────────────────

const ParentAccordion = ({
  category,
  edits,
  allEdits,
  parentOptions,
  selectedIds,
  onSelect,
  onParentChange,
  onChildChange,
  onSaveSingle,
  onResetSingle,
  onDelete,
  savingIds,
  expandedIds,
  onToggleExpand,
}) => {
  const isDirty = Object.keys(edits).length > 0;
  const status = getCategoryStatus(category);
  const statusCfg = STATUS_CONFIG[status];
  const isSelected = selectedIds.has(category.id);
  const children = category.children ?? [];
  const isExpanded = expandedIds.has(category.id);
  const saving = savingIds.has(category.id);

  const allChildrenSelected =
    children.length > 0 && children.every((c) => selectedIds.has(c.id));
  const someChildrenSelected = children.some((c) => selectedIds.has(c.id));

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => onToggleExpand(category.id)}
      elevation={0}
      sx={{
        border: isDirty ? '2px solid' : '1px solid',
        borderColor: isDirty ? 'warning.main' : 'divider',
        '&:before': { display: 'none' },
        borderRadius: '12px !important',
        mb: 2,
        overflow: 'hidden',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 72,
          pl: 1.5,
          pr: 2,
          background: isDirty
            ? 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1.5,
            mr: 1,
            overflow: 'hidden',
          },
        }}
      >
        <Checkbox
          checked={isSelected}
          indeterminate={!isSelected && someChildrenSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(category.id);
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <Avatar
          src={getCategoryImageUrl(category.imageUrl)}
          variant="rounded"
          sx={{ width: 48, height: 48, bgcolor: 'primary.50', flexShrink: 0 }}
        >
          <CategoryIcon sx={{ fontSize: 24, color: 'primary.main' }} />
        </Avatar>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {edits.name ?? category.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
            {category.offer_type && (
              <Chip
                size="small"
                label={
                  OFFER_TYPE_LABELS[category.offer_type] ?? category.offer_type
                }
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            )}
            {children.length > 0 && (
              <Chip
                size="small"
                label={`${children.length} sous-cat.`}
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            )}
          </Box>
        </Box>
        {isDirty && (
          <Chip
            size="small"
            label="Modifié"
            color="warning"
            icon={<EditIcon sx={{ fontSize: '14px !important' }} />}
            sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
          />
        )}
        <Chip
          size="small"
          label={statusCfg.label}
          color={statusCfg.color}
          sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Formulaire d'édition */}
          <Grid item xs={12} md={children.length > 0 ? 6 : 12}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
              sx={{ mb: 1.5 }}
            >
              ✏️ Modifier
            </Typography>
            <CategoryEditFields
              category={category}
              edits={edits}
              parentOptions={parentOptions}
              onChange={(field, value) =>
                onParentChange(category.id, field, value)
              }
              onSave={() => onSaveSingle(category.id)}
              onReset={() => onResetSingle(category.id)}
              saving={saving}
              isDirty={isDirty}
            />
            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete([category.id])}
              >
                Supprimer cette catégorie
              </Button>
            </Box>
          </Grid>

          {/* Sous-catégories */}
          {children.length > 0 && (
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  📂 Sous-catégories ({children.length})
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allChildrenSelected}
                      indeterminate={
                        !allChildrenSelected && someChildrenSelected
                      }
                      onChange={(e) => {
                        children.forEach((c) =>
                          onSelect(c.id, e.target.checked)
                        );
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption">Tout sélectionner</Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Box>
              <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                {children.map((child) => {
                  const childEdits = allEdits[child.id] ?? {};
                  const childDirty = Object.keys(childEdits).length > 0;
                  return (
                    <ChildAccordion
                      key={child.id}
                      category={child}
                      edits={childEdits}
                      parentOptions={parentOptions}
                      isSelected={selectedIds.has(child.id)}
                      onSelect={(id) => onSelect(id)}
                      onChange={(field, value) =>
                        onChildChange(child.id, field, value)
                      }
                      onSave={() => onSaveSingle(child.id)}
                      onReset={() => onResetSingle(child.id)}
                      onDelete={onDelete}
                      isDirty={childDirty}
                      saving={savingIds.has(child.id)}
                    />
                  );
                })}
              </Box>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

// ─── AdminCategoryListTab (composant principal) ───────────────────────────────

const AdminCategoryListTab = ({ onNotification }) => {
  const { categories, loading, error, updateCategory, deleteCategory } =
    useCategories();

  // ── State ──────────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    search: '',
    level: 'all',     // 'all' | 'parent' | 'child'
    parentId: 'all',  // 'all' | category id (number)
    status: 'all',    // 'all' | 'active' | 'inactive' | 'pending'
    offerType: 'all',
    context: 'all',
  });
  const [filtersOpen, setFiltersOpen] = useState(true);

  // { [categoryId]: { field: value, ... } }
  const [localEdits, setLocalEdits] = useState({});

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Bulk apply
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');

  const [savingIds, setSavingIds] = useState(new Set());
  const [globalSaving, setGlobalSaving] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    ids: [],
  });

  // ── Derived data ───────────────────────────────────────────────────────────

  const allFlat = useMemo(() => {
    const flat = [];
    const flatten = (cats) => {
      cats.forEach((cat) => {
        flat.push(cat);
        if (cat.children?.length > 0) flatten(cat.children);
      });
    };
    flatten(categories);
    return flat;
  }, [categories]);

  const parentCategories = useMemo(
    () => categories.filter((c) => !c.parentId),
    [categories]
  );

  const passesFilter = useCallback(
    (cat) => {
      const search = filters.search.toLowerCase();
      if (
        search &&
        !cat.name?.toLowerCase().includes(search) &&
        !cat.description?.toLowerCase().includes(search)
      )
        return false;
      if (filters.level === 'parent' && cat.parentId) return false;
      if (filters.level === 'child' && !cat.parentId) return false;
      if (filters.parentId !== 'all') {
        if (
          !cat.parentId ||
          String(cat.parentId) !== String(filters.parentId)
        )
          return false;
      }
      const status = getCategoryStatus(cat);
      if (filters.status !== 'all' && status !== filters.status) return false;
      if (filters.offerType !== 'all' && cat.offer_type !== filters.offerType)
        return false;
      if (filters.context !== 'all' && cat.context !== filters.context)
        return false;
      return true;
    },
    [filters]
  );

  // Vue aplatie (pour filtres "child only" ou par parent précis)
  const isFlat =
    filters.level === 'child' || filters.parentId !== 'all';

  const filteredItems = useMemo(() => {
    if (isFlat) {
      return allFlat.filter((cat) => passesFilter(cat));
    }
    // Vue hiérarchique : garder les parents qui passent OU qui ont des enfants qui passent
    const result = [];
    parentCategories.forEach((parent) => {
      const parentPasses = passesFilter(parent);
      const matchedChildren = (parent.children ?? []).filter((child) =>
        passesFilter(child)
      );
      if (parentPasses || matchedChildren.length > 0) {
        result.push({
          ...parent,
          // Si le parent passe lui-même : montrer tous ses enfants
          // Sinon : montrer seulement ceux qui passent le filtre
          children: parentPasses ? parent.children ?? [] : matchedChildren,
        });
      }
    });
    return result;
  }, [isFlat, allFlat, parentCategories, passesFilter]);

  const visibleFlat = useMemo(
    () => allFlat.filter((c) => passesFilter(c)),
    [allFlat, passesFilter]
  );

  const allSelected =
    visibleFlat.length > 0 && visibleFlat.every((c) => selectedIds.has(c.id));
  const someSelected = visibleFlat.some((c) => selectedIds.has(c.id));

  const totalModified = Object.keys(localEdits).length;
  const totalSelected = selectedIds.size;

  const activeFilterCount = Object.entries(filters).filter(([k, v]) =>
    k === 'search' ? v !== '' : v !== 'all'
  ).length;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleFilterChange = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleClearFilters = () =>
    setFilters({
      search: '',
      level: 'all',
      parentId: 'all',
      status: 'all',
      offerType: 'all',
      context: 'all',
    });

  const handleFieldChange = useCallback((categoryId, field, value) => {
    setLocalEdits((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], [field]: value },
    }));
  }, []);

  const handleResetCategory = useCallback((categoryId) => {
    setLocalEdits((prev) => {
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
  }, []);

  const handleResetAll = () => setLocalEdits({});

  const handleToggleSelect = useCallback((id, forceValue = null) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (forceValue !== null) {
        forceValue ? next.add(id) : next.delete(id);
      } else {
        next.has(id) ? next.delete(id) : next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleExpandAll = () =>
    setExpandedIds(new Set(filteredItems.map((c) => c.id)));
  const handleCollapseAll = () => setExpandedIds(new Set());

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(visibleFlat.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  // Sauvegarde individuelle
  const handleSaveSingle = useCallback(
    async (categoryId) => {
      const edits = localEdits[categoryId];
      if (!edits || Object.keys(edits).length === 0) return;
      setSavingIds((prev) => new Set([...prev, categoryId]));
      try {
        // Extraire le fichier et les flags spéciaux
        const { imageFile, imageUrlCleared, ...dataToSend } = edits;

        // Créer FormData si un fichier image est présent ou si on vient de le supprimer
        let finalData = dataToSend;
        if (imageFile || imageUrlCleared) {
          const formData = new FormData();
          
          // Ajouter tous les champs
          Object.entries(dataToSend).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formData.append(key, value);
            }
          });

          // Ajouter le fichier image s'il existe
          if (imageFile) {
            formData.append('categoryImage', imageFile);
          }

          // Indiquer qu'on veut supprimer l'image
          if (imageUrlCleared) {
            formData.append('clearImage', 'true');
          }

          finalData = formData;
        }

        await updateCategory(categoryId, finalData);
        handleResetCategory(categoryId);
        onNotification({ message: 'Catégorie sauvegardée avec succès', severity: 'success' });
      } catch (err) {
        onNotification({
          message:
            err.response?.data?.message || 'Erreur lors de la sauvegarde',
          severity: 'error',
        });
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryId);
          return next;
        });
      }
    },
    [localEdits, updateCategory, handleResetCategory, onNotification]
  );

  // Sauvegarde globale (toutes les catégories modifiées)
  const handleSaveAll = async () => {
    const ids = Object.keys(localEdits);
    if (ids.length === 0) return;
    setGlobalSaving(true);
    const errors = [];
    for (const id of ids) {
      try {
        const edits = localEdits[id];
        
        // Extraire le fichier et les flags spéciaux
        const { imageFile, imageUrlCleared, ...dataToSend } = edits;

        // Créer FormData si un fichier image est présent ou si on vient de le supprimer
        let finalData = dataToSend;
        if (imageFile || imageUrlCleared) {
          const formData = new FormData();
          
          // Ajouter tous les champs
          Object.entries(dataToSend).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formData.append(key, value);
            }
          });

          // Ajouter le fichier image s'il existe
          if (imageFile) {
            formData.append('categoryImage', imageFile);
          }

          // Indiquer qu'on veut supprimer l'image
          if (imageUrlCleared) {
            formData.append('clearImage', 'true');
          }

          finalData = formData;
        }

        await updateCategory(id, finalData);
        handleResetCategory(id);
      } catch {
        errors.push(id);
      }
    }
    setGlobalSaving(false);
    if (errors.length > 0) {
      onNotification({
        message: `${errors.length} catégorie(s) non sauvegardée(s)`,
        severity: 'warning',
      });
    } else {
      onNotification({
        message: `${ids.length} catégorie(s) sauvegardée(s) avec succès`,
        severity: 'success',
      });
    }
  };

  // Suppression confirmée
  const handleDeleteConfirmed = async () => {
    const ids = [...deleteConfirm.ids];
    setDeleteConfirm({ open: false, ids: [] });
    const errors = [];
    for (const id of ids) {
      try {
        await deleteCategory(id);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        handleResetCategory(id);
      } catch {
        errors.push(id);
      }
    }
    if (errors.length > 0) {
      onNotification({
        message: `${errors.length} catégorie(s) non supprimée(s)`,
        severity: 'error',
      });
    } else {
      onNotification({
        message: `${ids.length} catégorie(s) supprimée(s)`,
        severity: 'success',
      });
    }
  };

  // Application en masse d'un champ aux sélectionnés
  const handleBulkApply = () => {
    if (!bulkField || selectedIds.size === 0) return;
    let field, value;
    if (bulkField === 'isActive_true') {
      field = 'isActive';
      value = true;
    } else if (bulkField === 'isActive_false') {
      field = 'isActive';
      value = false;
    } else if (bulkField === 'offer_type') {
      field = 'offer_type';
      value = bulkValue;
    } else if (bulkField === 'context') {
      field = 'context';
      value = bulkValue;
    } else {
      return;
    }
    if (value === '' || value === null || value === undefined) return;

    setLocalEdits((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = { ...next[id], [field]: value };
      });
      return next;
    });
    onNotification({
      message: `Modification appliquée à ${selectedIds.size} catégorie(s) — pensez à sauvegarder`,
      severity: 'info',
    });
  };

  const showFloatingBar = totalModified > 0 || totalSelected > 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <Loader />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* ── Panneau de filtres ─────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* En-tête filtres */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            userSelect: 'none',
          }}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            <Typography variant="subtitle1" fontWeight={600}>
              Filtres avancés
            </Typography>
            {activeFilterCount > 0 && (
              <Chip
                size="small"
                label={`${activeFilterCount} actif${activeFilterCount > 1 ? 's' : ''}`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                }}
              />
            )}
          </Box>
          <ExpandMoreIcon
            sx={{
              transition: 'transform 0.3s',
              transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Recherche */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Rechercher par nom ou description..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: filters.search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleFilterChange('search', '')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Niveau */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Niveau</InputLabel>
                  <Select
                    value={filters.level}
                    label="Niveau"
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                  >
                    <MenuItem value="all">Tous les niveaux</MenuItem>
                    <MenuItem value="parent">Catégories parentes</MenuItem>
                    <MenuItem value="child">Sous-catégories</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Catégorie parente */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrer par parent</InputLabel>
                  <Select
                    value={filters.parentId}
                    label="Filtrer par parent"
                    onChange={(e) =>
                      handleFilterChange('parentId', e.target.value)
                    }
                  >
                    <MenuItem value="all">Tous les parents</MenuItem>
                    {parentCategories.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Statut */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={filters.status}
                    label="Statut"
                    onChange={(e) =>
                      handleFilterChange('status', e.target.value)
                    }
                  >
                    <MenuItem value="all">Tous les statuts</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="pending">En attente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Type d'offre */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type d'offre</InputLabel>
                  <Select
                    value={filters.offerType}
                    label="Type d'offre"
                    onChange={(e) =>
                      handleFilterChange('offerType', e.target.value)
                    }
                  >
                    <MenuItem value="all">Tous les types</MenuItem>
                    {OFFER_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Contexte */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Contexte</InputLabel>
                  <Select
                    value={filters.context}
                    label="Contexte"
                    onChange={(e) =>
                      handleFilterChange('context', e.target.value)
                    }
                  >
                    <MenuItem value="all">Tous</MenuItem>
                    {CONTEXT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Bouton reset */}
              <Grid item xs={12} sm={6} md={1}>
                <Tooltip title="Réinitialiser les filtres">
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    disabled={activeFilterCount === 0 && !filters.search}
                  >
                    Reset
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Stats rapides */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`${visibleFlat.length} résultat${visibleFlat.length > 1 ? 's' : ''}`}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`${allFlat.filter((c) => getCategoryStatus(c) === 'active').length} actives`}
                color="success"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${allFlat.filter((c) => getCategoryStatus(c) === 'inactive').length} inactives`}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${allFlat.filter((c) => getCategoryStatus(c) === 'pending').length} en attente`}
                color="warning"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${parentCategories.length} catégories parentes`}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${allFlat.filter((c) => !!c.parentId).length} sous-catégories`}
                color="secondary"
                variant="outlined"
              />
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* ── Barre d'outils ────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          px: 2,
          py: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Sélection globale */}
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={!allSelected && someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
          {someSelected
            ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''}`
            : `${visibleFlat.length} catégorie${visibleFlat.length !== 1 ? 's' : ''}`}
        </Typography>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Développer / Réduire (vue hiérarchique uniquement) */}
        {!isFlat && (
          <>
            <Tooltip title="Tout développer">
              <IconButton size="small" onClick={handleExpandAll}>
                <ExpandAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Tout réduire">
              <IconButton size="small" onClick={handleCollapseAll}>
                <CollapseAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

        {/* Application en masse */}
        {totalSelected > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              flexGrow: 1,
            }}
          >
            <Typography variant="body2" color="primary" fontWeight={600}>
              Appliquer à la sélection :
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={bulkField}
                label="Action"
                onChange={(e) => {
                  setBulkField(e.target.value);
                  setBulkValue('');
                }}
              >
                <MenuItem value="isActive_true">✅ Activer tout</MenuItem>
                <MenuItem value="isActive_false">❌ Désactiver tout</MenuItem>
                <MenuItem value="offer_type">🏷️ Changer le type d'offre</MenuItem>
                <MenuItem value="context">📋 Changer le contexte</MenuItem>
              </Select>
            </FormControl>

            {bulkField === 'offer_type' && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Valeur</InputLabel>
                <Select
                  value={bulkValue}
                  label="Valeur"
                  onChange={(e) => setBulkValue(e.target.value)}
                >
                  {OFFER_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {bulkField === 'context' && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Valeur</InputLabel>
                <Select
                  value={bulkValue}
                  label="Valeur"
                  onChange={(e) => setBulkValue(e.target.value)}
                >
                  {CONTEXT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {bulkField && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<BulkApplyIcon />}
                onClick={handleBulkApply}
                disabled={
                  (bulkField === 'offer_type' && !bulkValue) ||
                  (bulkField === 'context' && !bulkValue)
                }
              >
                Appliquer
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* ── Liste des catégories ──────────────────────────────────────────── */}
      {filteredItems.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <CategoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Aucune catégorie trouvée
          </Typography>
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={handleClearFilters}
          >
            Réinitialiser les filtres
          </Button>
        </Paper>
      ) : isFlat ? (
        // Vue aplatie (enfants seuls ou filtre par parent précis)
        filteredItems.map((cat) => {
          const catEdits = localEdits[cat.id] ?? {};
          const isDirty = Object.keys(catEdits).length > 0;
          return (
            <ChildAccordion
              key={cat.id}
              category={cat}
              edits={catEdits}
              parentOptions={parentCategories}
              isSelected={selectedIds.has(cat.id)}
              onSelect={handleToggleSelect}
              onChange={(field, value) => handleFieldChange(cat.id, field, value)}
              onSave={() => handleSaveSingle(cat.id)}
              onReset={() => handleResetCategory(cat.id)}
              onDelete={(ids) => setDeleteConfirm({ open: true, ids })}
              isDirty={isDirty}
              saving={savingIds.has(cat.id)}
            />
          );
        })
      ) : (
        // Vue hiérarchique (défaut)
        filteredItems.map((parentCat) => (
          <ParentAccordion
            key={parentCat.id}
            category={parentCat}
            edits={localEdits[parentCat.id] ?? {}}
            allEdits={localEdits}
            parentOptions={parentCategories}
            selectedIds={selectedIds}
            onSelect={handleToggleSelect}
            onParentChange={handleFieldChange}
            onChildChange={handleFieldChange}
            onSaveSingle={handleSaveSingle}
            onResetSingle={handleResetCategory}
            onDelete={(ids) => setDeleteConfirm({ open: true, ids })}
            savingIds={savingIds}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
          />
        ))
      )}

      {/* ── Barre d'action flottante ──────────────────────────────────────── */}
      <AnimatePresence>
        {showFloatingBar && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1300,
            }}
          >
            <Paper
              elevation={12}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background:
                  'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                color: 'white',
                boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                flexWrap: 'wrap',
                maxWidth: '90vw',
              }}
            >
              {/* Section modifications */}
              {totalModified > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <EditIcon sx={{ fontSize: 18, color: '#ffd700' }} />
                    <Typography
                      variant="body2"
                      sx={{ color: '#ffd700', fontWeight: 700 }}
                    >
                      {totalModified} modif{totalModified > 1 ? 's' : ''}.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveAll}
                    disabled={globalSaving}
                    sx={{
                      bgcolor: '#4CAF50',
                      '&:hover': { bgcolor: '#388E3C' },
                      fontWeight: 700,
                      borderRadius: 2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {globalSaving
                      ? 'Sauvegarde...'
                      : `Tout sauvegarder (${totalModified})`}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={handleResetAll}
                    sx={{
                      color: '#ff9800',
                      borderColor: '#ff9800',
                      '&:hover': {
                        borderColor: '#f57c00',
                        bgcolor: 'rgba(255,152,0,0.1)',
                      },
                      borderRadius: 2,
                    }}
                  >
                    Annuler tout
                  </Button>
                </>
              )}

              {/* Séparateur entre sections */}
              {totalModified > 0 && totalSelected > 0 && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                />
              )}

              {/* Section sélection */}
              {totalSelected > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CheckBoxIcon sx={{ fontSize: 18, color: '#90caf9' }} />
                    <Typography
                      variant="body2"
                      sx={{ color: '#90caf9', fontWeight: 700 }}
                    >
                      {totalSelected} sélectionné{totalSelected > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() =>
                      setDeleteConfirm({
                        open: true,
                        ids: Array.from(selectedIds),
                      })
                    }
                    sx={{ fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap' }}
                  >
                    Supprimer ({totalSelected})
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedIds(new Set())}
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.6)',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                      borderRadius: 2,
                    }}
                  >
                    Désélectionner
                  </Button>
                </>
              )}
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Espace en bas pour que la barre flottante ne cache pas le contenu */}
      {showFloatingBar && <Box sx={{ height: 88 }} />}

      {/* ── Dialogue de confirmation de suppression ───────────────────────── */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Confirmer la suppression"
        content={
          deleteConfirm.ids.length > 1
            ? `Êtes-vous sûr de vouloir supprimer ces ${deleteConfirm.ids.length} catégories ? Cette action est irréversible.`
            : 'Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.'
        }
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteConfirm({ open: false, ids: [] })}
      />
    </Box>
  );
};

export default AdminCategoryListTab;
